import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useSchoolId } from "../lib/auth/schoolContext";
import AppLayout from "../components/AppLayout";
import { Icons } from "../lib/Icons";
import SchoolPageHeader from "../components/school/SchoolPageHeader";
import { studentHasEhcp } from "../lib/students";

const STATUS_FILTERS = [
  { value: "all", label: "All students" },
  { value: "ehcp", label: "EHCP" },
  { value: "sen_support", label: "SEN Support" },
];

function getPlanStatus(student) {
  if (studentHasEhcp(student)) return "ehcp";
  return "sen_support";
}

function statusBadge(status) {
  switch (status) {
    case "ehcp":
      return { label: "EHCP", className: "bg-green-100 text-green-800 border-green-200" };
    case "sen_support":
      return { label: "SEN Support", className: "bg-blue-100 text-blue-800 border-blue-200" };
    default:
      return { label: "Monitoring", className: "bg-gray-100 text-gray-700 border-gray-200" };
  }
}

function lastUpdatedLabel(student) {
  const candidates = [
    student.voiceUpdatedAt,
    student.apdrSupportPlanUpdatedAt,
    student.apdrCyclesUpdatedAt,
    student.updatedAt,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      const date = candidate?.toDate ? candidate.toDate() : new Date(candidate);
      if (!Number.isNaN(date.getTime())) {
        return new Intl.DateTimeFormat("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(date);
      }
    } catch (_) {
      // ignore
    }
  }
  return "Not yet updated";
}

function richnessSummary(student) {
  const flags = [];
  if (student.pupilVoice && Object.values(student.pupilVoice).some((v) => v?.trim?.())) {
    flags.push("Pupil voice");
  }
  if (student.parentVoice && (student.parentVoice.aspirations || student.parentVoice.concerns)) {
    flags.push("Parent voice");
  }
  const apdrCycleCount = (() => {
    if (student.apdrSupportPlan?.cycles?.length) {
      return student.apdrSupportPlan.cycles.filter(
        (c) => c.reviewDate && ((c.rows?.length > 0) || c.legacyNotes || c.legacyOutcome)
      ).length;
    }
    return Array.isArray(student.apdrCycles) ? student.apdrCycles.length : 0;
  })();
  if (apdrCycleCount > 0) {
    flags.push(`${apdrCycleCount} APDR ${apdrCycleCount === 1 ? "cycle" : "cycles"}`);
  }
  if (studentHasEhcp(student)) {
    flags.push("EHCP linked");
  }
  return flags;
}

export default function IndividualSupportPlansPage() {
  const { schoolId } = useSchoolId();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;
    const fetchStudents = async () => {
      try {
        setLoading(true);
        setError(null);
        const snap = await getDocs(
          query(collection(db, "students"), where("schoolId", "==", schoolId))
        );
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const aName = `${a.lastName || ""} ${a.firstName || ""}`.trim().toLowerCase();
          const bName = `${b.lastName || ""} ${b.firstName || ""}`.trim().toLowerCase();
          return aName.localeCompare(bName);
        });
        if (!cancelled) setStudents(list);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching students for ISPs page:", err);
          setError("Failed to load Individual Support Plans. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStudents();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students.filter((student) => {
      const status = getPlanStatus(student);
      if (filter !== "all" && status !== filter) return false;
      if (!term) return true;
      const name = `${student.firstName || ""} ${student.lastName || ""}`.toLowerCase();
      return name.includes(term) || (student.yearGroup || "").toLowerCase().includes(term);
    });
  }, [students, filter, search]);

  const counts = useMemo(() => {
    return {
      all: students.length,
      ehcp: students.filter((s) => getPlanStatus(s) === "ehcp").length,
      sen_support: students.filter((s) => getPlanStatus(s) === "sen_support").length,
    };
  }, [students]);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading Individual Support Plans...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-100 flex items-center justify-center">
              <Icons.ExclamationTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Individual Support Plans</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <SchoolPageHeader
            title="Individual Support Plans"
            description="One comprehensive record per student covering pupil and parent voice, EHCP evidence, interventions, and APDR cycles. Downloadable for EHCNA requests and parent meetings."
            icon={Icons.AcademicCap}
            breadcrumbs={[
              { label: "Dashboard", href: "/dashboard" },
              { label: "Individual Support Plans" },
            ]}
            actions={[
              { label: "Open Students List", href: "/student-organization", variant: "secondary" },
            ]}
          />
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                  filter === opt.value
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {opt.label}
                <span className="ml-2 text-xs opacity-80">{counts[opt.value] ?? 0}</span>
              </button>
            ))}
          </div>
          <div>
            <label className="sr-only" htmlFor="isp-search">Search students</label>
            <input
              id="isp-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or year group"
              className="w-full md:w-72 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-md border border-gray-100">
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-blue-100 flex items-center justify-center">
              <Icons.BookOpen className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">No matching plans</h2>
            <p className="text-gray-500 max-w-md mx-auto">
              Adjust the filter or search to find a student. Every student in your school has an ISP record by default.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((student) => {
              const status = getPlanStatus(student);
              const badge = statusBadge(status);
              const flags = richnessSummary(student);
              return (
                <Link
                  key={student.id}
                  to={`/student/${student.id}`}
                  className="block bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-indigo-200 transition-all hover:scale-[1.01] overflow-hidden"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="p-2 bg-indigo-100 rounded-xl">
                          <Icons.User className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {student.firstName} {student.lastName}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5 truncate">
                            {student.yearGroup || "Year unknown"}
                            {student.workingAtLevel ? ` • Working at ${student.workingAtLevel}` : ""}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex shrink-0 items-center px-2 py-0.5 text-xs rounded-full border ${badge.className}`}>
                        {badge.label}
                      </span>
                    </div>

                    {Array.isArray(student.primaryNeeds) && student.primaryNeeds.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {student.primaryNeeds.slice(0, 3).map((need) => (
                          <span key={need} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">
                            {need}
                          </span>
                        ))}
                      </div>
                    )}

                    {flags.length > 0 && (
                      <ul className="space-y-1 text-xs text-gray-600">
                        {flags.map((flag) => (
                          <li key={flag} className="flex items-center gap-1.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                            {flag}
                          </li>
                        ))}
                      </ul>
                    )}

                    <p className="mt-4 text-xs text-gray-400">
                      Last updated: {lastUpdatedLabel(student)}
                    </p>
                  </div>
                  <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-medium text-indigo-600">Open ISP</span>
                    <Icons.ChevronRight className="h-4 w-4 text-indigo-600" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {students.length > 0 && (
          <div className="mt-8 bg-white rounded-2xl shadow-md border border-gray-100 p-6">
            <h3 className="text-base font-semibold text-gray-700 mb-4">Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-indigo-50 rounded-xl">
                <div className="text-3xl font-bold text-indigo-600">{counts.all}</div>
                <div className="text-sm text-gray-600 mt-1">Students with an ISP</div>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <div className="text-3xl font-bold text-emerald-600">{counts.ehcp}</div>
                <div className="text-sm text-gray-600 mt-1">On EHCP</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600">{counts.sen_support}</div>
                <div className="text-sm text-gray-600 mt-1">SEN Support only</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
